import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { AdminCategoryStat, AdminTrendPoint } from "@/types";

const PRIMARY = "oklch(var(--primary-500))";
const ACCENT = "oklch(var(--accent-500))";
const GRID = "oklch(var(--background-300))";
const AXIS = "oklch(var(--foreground-500))";

interface TipPayload {
  name?: string;
  value?: number;
  color?: string;
}

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: TipPayload[]; label?: string }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-md border border-background-200 bg-background-50 px-3 py-2">
      <p className="text-xs font-bold text-foreground-950">{label}</p>
      {payload.map((p) => (
        <p key={p.name} className="mt-1 flex items-center gap-1.5 text-xs text-foreground-600">
          <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="capitalize">{p.name}</span>:{" "}
          <span className="font-semibold text-foreground-900">{p.value?.toLocaleString()}</span>
        </p>
      ))}
    </div>
  );
}

export function AdminTrafficChart({ data }: { data: AdminTrendPoint[] }) {
  return (
    <div className="h-72 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 10, right: 8, left: -8, bottom: 0 }}>
          <defs>
            <linearGradient id="adminViews" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={PRIMARY} stopOpacity={0.45} />
              <stop offset="100%" stopColor={PRIMARY} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
          <XAxis dataKey="label" tick={{ fill: AXIS, fontSize: 12 }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fill: AXIS, fontSize: 12 }} axisLine={false} tickLine={false} width={56} />
          <Tooltip content={<ChartTooltip />} />
          <Area type="monotone" dataKey="views" stroke={PRIMARY} strokeWidth={2.5} fill="url(#adminViews)" name="views" />
          <Line type="monotone" dataKey="listings" stroke={ACCENT} strokeWidth={2.5} dot={false} name="listings" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function AdminCategoryChart({ data }: { data: AdminCategoryStat[] }) {
  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
          <XAxis type="number" tick={{ fill: AXIS, fontSize: 12 }} axisLine={false} tickLine={false} />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fill: AXIS, fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            width={150}
          />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: "oklch(var(--background-100))" }} />
          <Bar dataKey="count" fill={PRIMARY} radius={[0, 6, 6, 0]} name="listings" barSize={18} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}